<template>
<div
  id="PrintArea"
  class="report-container"
>
  <div
    v-for="page in pdfDocumentPages"
    :id="`Page${page}`"
    :key="`page${page}`"
  >
    <canvas
      class="print el-canvas"
      :id="`the_canvas${page}`"
    />
    <div :id="`annotationLayer${page}`" class="annotationLayer"></div>
  </div>
</div>
</template>

<script>
import { GlobalWorkerOptions } from 'pdfjs-dist';
import updateCSSVariable from '@/utils/stylesheetManager';

GlobalWorkerOptions.workerSrc = require('pdfjs-dist/build/pdf.worker.entry.js');

function throttle(func, limit) {
  let timeoutId;
  let lastRan;
  return (...args) => {
    const nowTime = Date.now();
    if (!lastRan) {
      func(...args);
      lastRan = nowTime;
      timeoutId = setTimeout(() => {
        lastRan = undefined;
        clearTimeout(timeoutId);
      }, limit);
    }
  };
}

export default {
  props: {
    src: {
      type: String,
      default: '',
      validator: (value) => value.endsWith('.pdf'),
    },
  },
  data() {
    return {
      pdfScale: 3, // pdf放大系数
      pdfDocumentPages: [],
      pdfViewerWidth: 0,
      minZoom: 3,
      maxZoom: 5,
      zoomStep: 0.5,
      pdfViewer: null,
      renderTasks: {},
      linkService: null,
      currentRenderingPage: [],
      styleSheetId: 'root-style-sheet', // Updated to use existing stylesheet ID
    };
  },
  async mounted() {
    if (!this.isMobileDevice()) {
      // 因為需要使用e.preventDefault()，所以在mounted時加上{ passive: false }
      window.addEventListener('wheel', this.throttledHandleWheel, { passive: false });
    } else {
      // 部分行動裝置因為記憶體不夠，所以預設放大比例為1.5，避免遇到memory leak
      this.pdfScale = 1.5;
      this.minZoom = 1.5;
      this.maxZoom = 1.5;
    }
    await this.retrievePdfUrl();
    const observer = new IntersectionObserver(async ([entry]) => {
      const page = parseInt(entry.target.id.split('Page')[1], 10);
      if (!entry.isIntersecting) {
        const idx = this.currentRenderingPage.indexOf(page);
        if (idx < 0) return;
        await this.clearContent(page);
        this.currentRenderingPage.splice(idx, 1);
        return;
      }
      if (this.currentRenderingPage.includes(page)) return;
      this.renderPage(page);
    }, {
      rootMargin: '10% 0px 0px 0px',
      threshold: [0, 1],
    });
    for (let i = 1; i <= 2; i += 1) {
      this.renderPage(i);
    }
    this.pdfDocumentPages.forEach((page) => {
      const target = document.getElementById(`Page${page}`);
      observer.observe(target);
    });
    window.addEventListener('click', this.jumpToLink);
  },

  beforeUnmount() {
    if (!this.isMobileDevice()) {
      window.removeEventListener('wheel', this.throttledHandleWheel, { passive: false });
    }
    window.removeEventListener('click', this.jumpToLink);
  },

  created() {
    const throttleWheelFunction = throttle(this.handleWheel, 0.1 * 1000);
    this.throttledHandleWheel = (e) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        throttleWheelFunction(e);
      }
    };
  },
  watch: {
    pdfScale() {
      this.cancelAllRenderTasks();
      // Clear out current rendering pages to start fresh
      const pagesToRender = [...this.currentRenderingPage];
      this.currentRenderingPage = [];

      // Add a small delay before re-rendering to ensure clean canvas state
      setTimeout(() => {
        pagesToRender.forEach((page, index) => {
          setTimeout(() => {
            this.renderPage(page);
          }, index * 50); // Stagger the re-renders to avoid overwhelming the browser
        });
      }, 100);
    },
    pdfViewerWidth() {
      updateCSSVariable('--report-container-width', `${this.pdfViewerWidth}px`);
    },
  },
  methods: {
    cancelAllRenderTasks() {
      Object.values(this.renderTasks).forEach((task) => {
        if (task && typeof task.cancel === 'function') {
          task.cancel();
        }
      });
      this.renderTasks = {};
    },
    async clearContent(num) {
      if (this.renderTasks[num] && typeof this.renderTasks[num].cancel === 'function') {
        this.renderTasks[num].cancel();
        delete this.renderTasks[num];
      }

      try {
        const page = await this.pdfDoc.getPage(num);
        page.cleanup();

        // Clear the canvas to ensure a fresh state
        const canvas = document.getElementById(`the_canvas${num}`);
        if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        // Clear annotation layer
        const annotationLayer = document.getElementById(`annotationLayer${num}`);
        if (annotationLayer) {
          annotationLayer.innerHTML = '';
        }
      } catch (e) {
        console.error(`Error clearing content for page ${num}:`, e);
      }
    },
    async jumpToLink(e) {
      const { target: { href, classList, nodeName } } = e;
      // 如果不是內部連結，則不另外處理
      if (!(nodeName === 'A' && href && classList.contains('internalLink'))) return;
      e.preventDefault();
      // Decode Uri to get PDF internal link parameter
      const result = JSON.parse(decodeURIComponent(href).split('#').pop());
      /**
       * @param {Object} idxHash - PDF 內部錨點連結的hash值
       * @param {Object} align - PDF 內部錨點目標的對齊方式
       * @param {Number} distFromBottom - PDF 內部錨點目標距離PDF底部的距離
       */
      const [idxHash, align, , distFromBottom] = result;
      const findPageIndex = await this.pdfDoc.getPageIndex(idxHash);
      const pageNum = findPageIndex + 1;
      /**
       * @param {HTMLElement} targetEl - 跳轉頁面的容器元素
       */
      const targetEl = document.getElementById(`Page${pageNum}`);
      /**
       * @param {HTMLCanvasElement} canvas - 跳轉頁面的PDF畫布元素
       */
      const canvas = document.getElementById(`the_canvas${pageNum}`);
      switch (align.name) {
        // 純頁面跳轉(跳到指定頁數的頁首就好)
        case 'FitR':
          targetEl.scrollIntoView();
          break;
        // 特定元素區塊跳轉(跳到指定元素區塊)
        case 'XYZ':
          // 先找到頁首為起點，再根據目標區域的距離向下捲動讓目標區域在畫面上齊頭顯示
          window.scrollTo({
            top: targetEl.offsetTop + (
              // 因為畫面顯示會改變等比例改變PDFjs-dist畫出來的canvas大小，這邊需要畫面上實際的元素高度，所以取canvas、容器高度的最小值
              Math.min(targetEl.clientHeight, canvas.height)
              // 這邊PDFjs註解回傳的是PDF底部距離畫布底部的距離，所以要乘上PDF縮放比例，再乘上渲染後被縮放的比例，因應不同裝置，
              // 乘上解離率，才會是實際視窗卷軸要多滾動的差值
              - distFromBottom * this.pdfScale * (targetEl.clientHeight / canvas.height) * window.devicePixelRatio
            ),
          });
          break;
        default:
          break;
      }
    },
    handleWheel(e) {
      const delta = e.deltaY || e.detail || e.wheelDelta;
      /**
       * 滑鼠滾動事件:
       * delta > 0: 向下滾動，縮小
       * delta < 0: 向上滾動，放大
       */
      const fnZoom = delta > 0 ? this.zoomOut : this.zoomIn;
      fnZoom();
    },
    isMobileDevice() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },
    async retrievePdfUrl() {
      await this.loadFile(this.src);
    },
    async loadFile(url) {
      // 初始化pdf
      let pdf = null;
      try {
        const { getDocument } = await import('pdfjs-dist');
        pdf = await getDocument({ url }).promise;
        // 將獲取的PDF物件存進data，方便後續縮放重複使用
        this.pdfDoc = pdf;
        this.pdfDocumentPages = Array.from({ length: pdf.numPages }, (_, i) => i + 1);
      } catch (err) {
        alert(JSON.stringify(err));
      }
    },
    async renderPage(num) {
      // First ensure any existing render is cancelled
      if (this.renderTasks[num] && typeof this.renderTasks[num].cancel === 'function') {
        this.renderTasks[num].cancel();
        delete this.renderTasks[num];
      }

      // Ensure the page isn't already being rendered
      if (!this.currentRenderingPage.includes(num)) {
        this.currentRenderingPage.push(num);
        this.currentRenderingPage.sort();
      }

      try {
        const page = await this.pdfDoc.getPage(num);
        const canvas = document.getElementById(`the_canvas${num}`);
        if (!canvas) return;

        // Clear the canvas first
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // Continue with rendering
        const { devicePixelRatio: dpr = 1, innerWidth } = window;
        const viewport = page.getViewport({ scale: this.pdfScale < this.minZoom ? this.minZoom : this.pdfScale });

        // Reset canvas dimensions
        canvas.width = viewport.width * dpr;
        canvas.height = viewport.height * dpr;

        const newWidth = Math.min(canvas.width, innerWidth);
        if (!this.pdfViewerWidth || this.pdfViewerWidth > newWidth) {
          this.pdfViewerWidth = newWidth;
        }

        ctx.scale(dpr, dpr);

        const renderContext = {
          canvasContext: ctx,
          viewport,
        };

        // Store the render task
        this.renderTasks[num] = page.render(renderContext);
        await this.renderTasks[num].promise;
        delete this.renderTasks[num];

        // Need to wait for canvas rendering before rendering PDF links
        this.$nextTick(async () => {
          await this.setAnnotations(page, viewport, dpr);
        });
      } catch (error) {
        if (error.message !== 'Rendering cancelled') {
          console.error(`Error rendering page ${num}:`, error);
        }
        delete this.renderTasks[num];

        // Remove page from currentRenderingPage if there was an error
        const idx = this.currentRenderingPage.indexOf(num);
        if (idx >= 0) {
          this.currentRenderingPage.splice(idx, 1);
        }
      }
    },
    // PDF內連結
    async setAnnotations(page, viewport) {
      const { PDFLinkService, LinkTarget } = await import('pdfjs-dist/lib/web/pdf_link_service');
      const annotationLayerDiv = document.getElementById(`annotationLayer${page.pageNumber}`);
      if (!annotationLayerDiv) return;
      const annotations = await page.getAnnotations();
      const linkService = new PDFLinkService({
        externalLinkTarget: LinkTarget.BLANK,
      });
      const { AnnotationLayer } = await import('pdfjs-dist');
      AnnotationLayer.render({
        viewport: viewport.clone({ dontFlip: true }),
        div: annotationLayerDiv,
        annotations,
        page,
        linkService,
        renderInteractiveForms: true,
      });
      annotationLayerDiv.removeAttribute('style');
    },
    /**
     * @function zoomIn
     * @description 放大畫面解析度/顯示比例(如果超過最大解析度，則以最大解析度渲染並且等比例放大PDF圖片)
     * @param scale canvas解析度
     */
    zoomIn(scale = this.pdfScale + this.zoomStep) {
      if (scale > this.maxZoom) {
        const scaleRatio = (scale - this.maxZoom) / this.zoomStep;
        this.pdfViewerWidth *= Math.min(100 + (scaleRatio * 10), 200) / 100;
        console.log('zoomIn', this.pdfViewerWidth);
        return;
      }
      this.pdfScale = scale;
    },
    /**
     * @function zoomIn
     * @description 所小畫面解析度/顯示比例(如果超過最大解析度，則以最大解析度渲染並且等比例放大PDF圖片)
     * @param scale canvas解析度
     */
    zoomOut(scale = this.pdfScale - this.zoomStep) {
      if (scale < this.minZoom) {
        const scaleRatio = (this.minZoom - scale) / this.zoomStep;
        this.pdfViewerWidth *= Math.max(100 - (scaleRatio * 10), 10) / 100;
        console.log('zoomOut', this.pdfViewerWidth);
        return;
      }
      this.pdfScale = scale;
    },
  },
};
</script>
<style lang="scss">
.print {
  display: block;
}
.report-container {
  margin: 0 auto;
  width: var(--report-container-width);
}
.pageContainer {
  position: relative;
}
.annotationLayer {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.linkAnnotation {
  position: absolute;
  a {
    display: block;
    width: 100%;
    height: 100%;
  }
}
.el-canvas {
  width: 100% !important;
}
@media print {
  .no-print {
    display: none;
  }
  #PrintArea {
    width: 100% !important;
  }
  @page {
    size: A4 portrait;
    :blank {
      display: none;
    }
  }
}
</style>
